import { useMemo } from 'react'
import { Aurora, AuroraAssetGroup } from '../types'
import {
  AuroraBoilerMainPowerStatus,
  AuroraBoilerRunPermitStatus,
  AuroraCirculationPumpMode,
  AuroraCirculationPumpStatus,
  AuroraHeatPumpMode,
  AuroraHeatPumpStatus
} from '../enums'
import { AuroraAwhpActionNack } from '../errors'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { createErrorNotification } from '../redux/slices/notifications'
import { checkIsAuroraAwhp, checkIsAuroraBackup } from '../utils/aurora'

export const useAuroraStack = (auroraAssetGroup: AuroraAssetGroup) => {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const auroras = auroraAssetGroup.assets
    .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
    .map(({ aurora, ...asset }) => ({ ...aurora, asset }) as Aurora)

  const master = useMemo(() => {
    if (auroraAssetGroup.customData?.masterId === undefined) {
      return null
    }

    return auroras.find((aurora) => aurora.id === auroraAssetGroup.customData?.masterId) ?? null
  }, [auroras, auroraAssetGroup?.customData?.masterId])

  const positions = useMemo(() => {
    if (auroraAssetGroup.customData?.positions === undefined) {
      return []
    }

    return Object.values(auroraAssetGroup.customData.positions)
  }, [auroraAssetGroup.customData?.positions])

  const masterPosition = useMemo(() => {
    if (
      master?.id === undefined ||
      master?.id === null ||
      auroraAssetGroup.customData?.positions === undefined
    ) {
      return null
    }

    return auroraAssetGroup.customData.positions[master.id] || null
  }, [auroraAssetGroup.customData.positions, master?.id])

  const awhpAuroras = useMemo(() => auroras.filter(checkIsAuroraAwhp), [auroras])

  const backupAuroras = useMemo(() => auroras.filter(checkIsAuroraBackup), [auroras])

  const hasAwhpUnits = awhpAuroras.length > 0

  const hasBackupUnits = backupAuroras.length > 0

  const awhpMaster = master !== null && checkIsAuroraAwhp(master) ? master : null

  const heatPumpStatus = useMemo(
    () =>
      awhpAuroras.length > 0 && awhpAuroras.every((aurora) => aurora.isHeatPumpOn)
        ? AuroraHeatPumpStatus.ON
        : AuroraHeatPumpStatus.OFF,
    [awhpAuroras]
  )

  const heatPumpMode = useMemo(() => {
    if (awhpMaster !== null) {
      return awhpMaster.heatPumpOperationMode ?? AuroraHeatPumpMode.OFF
    }

    if (awhpAuroras.length === 0) {
      return AuroraHeatPumpMode.OFF
    }

    const [firstAurora, ...restAuroras] = awhpAuroras

    const everyModeMatches = restAuroras.every(
      (aurora) => aurora.heatPumpOperationMode === firstAurora.heatPumpOperationMode
    )

    if (everyModeMatches) {
      return firstAurora.heatPumpOperationMode ?? AuroraHeatPumpMode.OFF
    }

    return AuroraHeatPumpMode.OFF
  }, [awhpMaster, awhpAuroras])

  const boilerRunPermitStatus = useMemo(
    () =>
      backupAuroras.length > 0 && backupAuroras.every((aurora) => aurora.isBoilerRunPermitOn)
        ? AuroraBoilerRunPermitStatus.ON
        : AuroraBoilerRunPermitStatus.OFF,
    [backupAuroras]
  )

  const boilerMainPowerStatus = useMemo(
    () =>
      backupAuroras.length > 0 && backupAuroras.every((aurora) => aurora.isBoilerMainPowerOn)
        ? AuroraBoilerMainPowerStatus.ON
        : AuroraBoilerMainPowerStatus.OFF,
    [backupAuroras]
  )

  const targetTemperature = useMemo(() => {
    if (awhpMaster !== null) {
      return awhpMaster.targetTemperature1
    }

    if (awhpAuroras.length === 0) {
      return null
    }

    return Math.max(...awhpAuroras.map((aurora) => aurora.targetTemperature1))
  }, [awhpMaster, awhpAuroras])

  const circulationPumpStatus = useMemo(
    () =>
      auroras.every((aurora) => aurora.isCirculationPumpOn)
        ? AuroraCirculationPumpStatus.ON
        : AuroraCirculationPumpStatus.OFF,
    [auroras]
  )

  const circulationPumpMode = useMemo(() => {
    if (master !== null) {
      return master.circulationPumpMode ?? AuroraCirculationPumpMode.AUTO
    }

    const everyIsAuto = auroras.every(
      (aurora) => aurora.circulationPumpMode === AuroraCirculationPumpMode.AUTO
    )

    if (everyIsAuto) {
      return AuroraCirculationPumpMode.AUTO
    }

    const everyIsMax = auroras.every(
      (aurora) => aurora.circulationPumpMode === AuroraCirculationPumpMode.MAX
    )

    if (everyIsMax) {
      return AuroraCirculationPumpMode.MAX
    }

    const everyIsMin = auroras.every(
      (aurora) => aurora.circulationPumpMode === AuroraCirculationPumpMode.MIN
    )

    if (everyIsMin) {
      return AuroraCirculationPumpMode.MIN
    }

    return AuroraCirculationPumpMode.AUTO
  }, [master])

  const minTemperatureLimit = useMemo(() => {
    if (master !== null) {
      return master.minTemperatureLimit
    }

    if (auroras.length === 0) {
      return null
    }

    return Math.max(...auroras.map((aurora) => aurora.minTemperatureLimit))
  }, [master, auroras])

  const maxTemperatureLimit = useMemo(() => {
    if (master !== null) {
      return master.maxTemperatureLimit
    }

    if (auroras.length === 0) {
      return null
    }

    return Math.min(...auroras.map((aurora) => aurora.maxTemperatureLimit))
  }, [master, auroras])

  const minPressureLimit = useMemo(() => {
    if (master !== null) {
      return master.minPressureLimit
    }

    if (auroras.length === 0) {
      return null
    }

    return Math.max(...auroras.map((aurora) => aurora.minPressureLimit))
  }, [master, auroras])

  const maxPressureLimit = useMemo(() => {
    if (master !== null) {
      return master.maxPressureLimit
    }

    if (auroras.length === 0) {
      return null
    }

    return Math.min(...auroras.map((aurora) => aurora.maxPressureLimit))
  }, [master, auroras])

  const minFlowLimit = useMemo(() => {
    if (master !== null) {
      return master.minFlowLimit
    }

    if (auroras.length === 0) {
      return null
    }

    return Math.max(...auroras.map((aurora) => aurora.minFlowLimit))
  }, [master, auroras])

  const handleAuroraAwhpActionError = (error: unknown, defaultMessage: string) => {
    if (error instanceof AuroraAwhpActionNack) {
      handleAuroraAwhpActionNack(error, defaultMessage)
    } else {
      dispatch(createErrorNotification(defaultMessage))
    }

    throw error
  }

  const handleAuroraAwhpActionNack = (error: AuroraAwhpActionNack, defaultMessage: string) => {
    switch (error.message) {
      case 'PIN_MISSING':
        dispatch(createErrorNotification(t('pinMissing')))
        break
      case 'PIN_REJECTED':
        dispatch(createErrorNotification(t('pinRejected')))
        break
      default:
        dispatch(createErrorNotification(defaultMessage))
        break
    }
  }

  return {
    auroras,
    awhpAuroras,
    backupAuroras,
    awhpMaster,
    master,
    masterPosition,
    positions,
    hasAwhpUnits,
    hasBackupUnits,
    heatPumpStatus,
    heatPumpMode,
    targetTemperature,
    circulationPumpStatus,
    circulationPumpMode,
    boilerRunPermitStatus,
    boilerMainPowerStatus,
    minTemperatureLimit,
    maxTemperatureLimit,
    minPressureLimit,
    maxPressureLimit,
    minFlowLimit,
    handleAuroraAwhpActionError
  }
}
